import { useSyncExternalStore } from 'react';

import { cn } from '@/lib/utils';

/**
 * Toast — a "sonner-lite" written by hand (05 §6.3).
 *
 * A tiny external store rather than context, so non-React code (the API client,
 * a socket handler) can raise a toast without a hook. Mount `<Toaster />` once,
 * at the app root; call `toast.success(...)` / `toast.error(...)` from anywhere.
 */

export type ToastVariant = 'default' | 'success' | 'error' | 'warning';

export interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  /** Milliseconds before auto-dismiss; `0` keeps it until dismissed. */
  duration: number;
}

export interface ToastInput {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
}

let items: ToastItem[] = [];
const listeners = new Set<() => void>();
let counter = 0;

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ToastItem[] {
  return items;
}

function dismiss(id: string): void {
  const next = items.filter((item) => item.id !== id);
  if (next.length === items.length) return;
  items = next;
  emit();
}

function show(input: ToastInput): string {
  counter += 1;
  const id = `toast-${counter}`;
  const item: ToastItem = {
    id,
    title: input.title,
    variant: input.variant ?? 'default',
    duration: input.duration ?? 5000,
    ...(input.description !== undefined ? { description: input.description } : {}),
  };
  // Newest first, and never more than 4 on screen at once.
  items = [item, ...items].slice(0, 4);
  emit();

  if (item.duration > 0) {
    setTimeout(() => dismiss(id), item.duration);
  }
  return id;
}

export const toast = {
  show,
  dismiss,
  message: (title: string, description?: string) =>
    show({ title, ...(description ? { description } : {}) }),
  success: (title: string, description?: string) =>
    show({ title, variant: 'success', ...(description ? { description } : {}) }),
  error: (title: string, description?: string) =>
    show({ title, variant: 'error', ...(description ? { description } : {}) }),
  warning: (title: string, description?: string) =>
    show({ title, variant: 'warning', ...(description ? { description } : {}) }),
} as const;

const VARIANT_CLASS: Record<ToastVariant, string> = {
  default: 'border-border bg-card text-card-foreground',
  success: 'border-success/40 bg-card text-card-foreground',
  error: 'border-destructive/50 bg-card text-card-foreground',
  warning: 'border-warning/50 bg-card text-card-foreground',
};

const ACCENT_CLASS: Record<ToastVariant, string> = {
  default: 'bg-muted-foreground',
  success: 'bg-success',
  error: 'bg-destructive',
  warning: 'bg-warning',
};

export interface ToasterProps {
  /** Localised label for the dismiss button. */
  closeLabel?: string;
}

export function Toaster({ closeLabel = 'Close' }: ToasterProps) {
  const toasts = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return (
    // `end-4` (not right-4) so the stack sits on the correct side in RTL and LTR.
    <div
      className="pointer-events-none fixed bottom-4 end-4 z-[60] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      role="region"
      aria-label="notifications"
    >
      {toasts.map((item) => (
        <div
          key={item.id}
          role="status"
          aria-live="polite"
          className={cn(
            'pointer-events-auto flex items-start gap-3 overflow-hidden rounded-lg border p-3 shadow-lg animate-fade-in',
            VARIANT_CLASS[item.variant],
          )}
        >
          <span
            className={cn(
              'mt-0.5 h-full w-1 shrink-0 self-stretch rounded',
              ACCENT_CLASS[item.variant],
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{item.title}</p>
            {item.description ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{item.description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => dismiss(item.id)}
            aria-label={closeLabel}
            className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden="true">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}

/** Test helper: drop every toast without waiting for the timers. */
export function clearToasts(): void {
  items = [];
  emit();
}
